import { TFunction } from 'i18next';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';
import {
  GetAllLogoSettingsResponse,
  GetAllLogosResponse,
  GetLogoSettingsResponse,
  LogoModel,
  LogoSettings,
  LogoSettingsViewModel,
} from './logos-models';
import videoInputs from '../video-input/video-input-store';
import { VIDIN_CONNECTOR } from '../video-encoder/video-encoder-models';
import systemInfo from 'src/js/data/systemInfo';

const logoStore: LogoModel[] = [];
createWatchStore(logoStore);
export { logoStore };

const logoSettingsStore: LogoSettingsViewModel[] = [];
createWatchStore(logoSettingsStore, { selectable: true });
export { logoSettingsStore };

export const fetchLogos = (_t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllLogosResponse>(APIPath.logos.all).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        updateWatchStore(logoStore, response.data);
      }
    }
    return res;
  });
};

export const fetchLogoSettings = (_t: TFunction) => {
  return apiClientWithoutHandler.genericController
    .get<GetAllLogoSettingsResponse>(APIPath.logosettings.all)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const logosViewModel = response.data.map((logoSetting) => {
            const viewModel: LogoSettingsViewModel = logoSetting;
            viewModel.url =
              logoSetting.fileNameUrlEncoded !== ''
                ? '/apis/logos/.' + logoSetting.fileNameUrlEncoded + '.oly.png?t=' + Date.now()
                : null;
            viewModel.panelColor = 'haiui-gray-04';
            if (viewModel.fileName) {
              viewModel.panelColor = 'haiui-gray-07';
            }
            if (viewModel.fileName && viewModel.display) {
              viewModel.panelColor = 'haiui-green-01';
            }
            return viewModel;
          });
          updateWatchStore(logoSettingsStore, logosViewModel);
        }
      }
      return res;
    });
};

export const updateLogoConfiguration = (model: Partial<LogoSettings>, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.logosettings.update, { interface: model.interface });
  return apiClientWithoutHandler.genericController
    .put<GetLogoSettingsResponse>(path, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          // FIXME replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        t('logos.notifications.changeSuccessMsg', { interface: getInterfaceLabelWithDigit(model, t) }),
        t('logos.notifications.changeErrorMsg', { interface: getInterfaceLabelWithDigit(model, t) }),
        t,
      );
      return res;
    });
};

export const uploadLogo = (file: File, t: TFunction, printSuccessNotifications: boolean) => {
  return apiClientWithoutHandler.genericController.upload(APIPath.logos.add, { file: file }).then((res) => {
    if (!res.ok && (res.problem === 'CONNECTION_ERROR' || res.problem === 'NETWORK_ERROR')) {
      return res; // no error notifcation, upload widget will display 'Try Again'
    }
    notificationHandler(
      res,
      printSuccessNotifications && t('logos.notifications.importSuccessMsg', { name: file.name }),
      t('logos.notifications.importErrorMsg', { name: file.name }),
      t,
    );
    return res;
  });
};

export const deleteLogo = (model: LogoModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.logos.delete, { name: model.name });
  return apiClientWithoutHandler.genericController.delete(path).then((res) => {
    if (!res.ok) {
      notificationHandler(
        res,
        t('logos.notifications.deleteSuccessMsg', { name: model.name }),
        t('logos.notifications.deleteErrorMsg', { name: model.name }),
        t,
      );
    }
    return res;
  });
};

export const getInterfaceLabelWithDigit = (logoSettingsViewModel: Partial<LogoSettingsViewModel>, t: TFunction) => {
  let interfaceInputType = -1;
  const videoInput = videoInputs.find((input: any) => input.id === logoSettingsViewModel.interface);
  if (videoInput !== undefined) {
    interfaceInputType = videoInput.interfaceType;
  }
  if (interfaceInputType === VIDIN_CONNECTOR.SDI) {
    if (systemInfo.videoInputs >= 2) {
      //MX1 has 1 video input, FX also has 1 (even when having both SDI & HDMI connectors)
      return `${t('streaming.logos.inputInterface.SDI')} ${logoSettingsViewModel.interface + 1}`;
    }
    return t('streaming.logos.inputInterface.SDI');
  } else if (interfaceInputType === VIDIN_CONNECTOR.HDMI) {
    return t('streaming.logos.inputInterface.HDMI');
  } else if (interfaceInputType === VIDIN_CONNECTOR.DISPLAYPORT) {
    return t('streaming.logos.inputInterface.DISPLAYPORT');
  } else {
    return '';
  }
};

export const getInterfaceLabel = (logoSettingsViewModel: LogoSettingsViewModel, t: TFunction) => {
  let interfaceInputType = -1;
  const videoInput = videoInputs.find((input: any) => input.id === logoSettingsViewModel.interface);
  if (videoInput !== undefined) {
    interfaceInputType = videoInput.interfaceType;
  }
  if (interfaceInputType === VIDIN_CONNECTOR.SDI) {
    return t('streaming.logos.inputInterface.SDI');
  } else if (interfaceInputType === VIDIN_CONNECTOR.HDMI) {
    return t('streaming.logos.inputInterface.HDMI');
  } else if (interfaceInputType === VIDIN_CONNECTOR.DISPLAYPORT) {
    return t('streaming.logos.inputInterface.DISPLAYPORT');
  } else {
    return '';
  }
};
